import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useCatalogCategoryUsage,
  useCatalogCyclesUsage,
  useCatalogRowUsage,
} from "@/hooks/useCatalog";
import type { CatalogList } from "./useCatalogEditor";

/**
 * Every deactivation of a saved row asks first. With each edit saved the moment
 * it is made there is no «Descartar» any more, so this is the undo that
 * replaced it — and the one place the admin learns how many prospects still
 * point at what they are removing, read fresh when the dialog opens.
 */

export type DeactivateTarget = { list: CatalogList; key: string; id: string; name: string };

const WHAT_HAPPENS: Record<CatalogList, (noun: string) => string> = {
  products: (noun) =>
    `El agente deja de ofrecer este ${noun}. Sus prospectos, medios y pedidos se conservan, y puedes reactivarlo desde la lista de inactivos.`,
  cycles: () =>
    "El agente deja de ofrecer este ciclo. Los prospectos que lo tienen lo conservan. Para recuperarlo, agrega de nuevo un ciclo con la misma clave.",
  customFields: () =>
    "El agente deja de preguntar por este dato. Las respuestas ya capturadas se conservan. Para recuperarlo, agrega de nuevo un campo con el mismo nombre interno.",
  categories: (noun) =>
    `Sus ${noun}s no se desactivan: pasan a «Sin categoría» y el agente los sigue ofreciendo.`,
};

function prospects(n: number): string {
  return `${n} ${n === 1 ? "prospecto" : "prospectos"}`;
}

export function DeactivateConfirmDialog({
  target,
  noun,
  onConfirm,
  onClose,
}: {
  target: DeactivateTarget | null;
  /** What a product is called in this catalog: "programa" or "producto". */
  noun: string;
  onConfirm: (target: DeactivateTarget) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const isCategory = target?.list === "categories";
  // Two different counts: prospects for a row, products for a category.
  const { data: usage, isPending: rowPending } = useCatalogRowUsage(
    target?.list ?? "products",
    target && !isCategory ? target.id : null,
  );
  const { data: categoryUsage, isPending: categoryPending } = useCatalogCategoryUsage(
    target && isCategory ? target.id : null,
  );
  const isPending = isCategory ? categoryPending : rowPending;

  async function confirm(): Promise<void> {
    if (!target) return;
    setBusy(true);
    const ok = await onConfirm(target);
    setBusy(false);
    if (ok) onClose();
  }

  const leads = usage?.leads ?? 0;
  const products = categoryUsage?.products ?? 0;
  const countLine = isCategory
    ? products > 0
      ? `Tiene ${products} ${products === 1 ? noun : `${noun}s`}.`
      : `No tiene ${noun}s.`
    : leads > 0
      ? `Tiene ${prospects(leads)} ligados.`
      : "No tiene prospectos ligados.";

  return (
    <AlertDialog open={target !== null} onOpenChange={(open: boolean) => !open && !busy && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Desactivar «{target?.name}»?</AlertDialogTitle>
          <AlertDialogDescription>
            {target ? WHAT_HAPPENS[target.list](noun) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <p className="text-[13px] text-ink-700">
          {isPending ? "Revisando…" : countLine}
        </p>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={(e) => {
              // Stays open until the server answers, so a failure is not a
              // dialog that closed as if it had worked.
              e.preventDefault();
              void confirm();
            }}
          >
            {busy ? "Desactivando…" : "Desactivar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Turning the cycles switch off (open enrollment) deactivates every cycle in one
 * step — the two are contradictory. The dialog names the cycles and the
 * prospects on them.
 */
export function OpenEnrollmentDialog({
  open,
  cycles,
  onConfirm,
  onClose,
}: {
  open: boolean;
  cycles: Array<{ id: string; key: string; label: string }>;
  onConfirm: () => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const usage = useCatalogCyclesUsage(
    cycles.map((c) => c.id),
    open,
  );

  async function confirm(): Promise<void> {
    setBusy(true);
    const ok = await onConfirm();
    setBusy(false);
    if (ok) onClose();
  }

  return (
    <AlertDialog open={open} onOpenChange={(next: boolean) => !next && !busy && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Dejar de usar ciclos?</AlertDialogTitle>
          <AlertDialogDescription>
            El agente dirá que se puede empezar en cualquier momento, y{" "}
            {cycles.length === 1 ? "el ciclo configurado se desactiva" : `los ${cycles.length} ciclos configurados se desactivan`}
            . Los prospectos conservan el ciclo que ya tenían.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="flex flex-col gap-1 text-[13px] text-ink-700">
          {cycles.map((cycle, i) => {
            const leads = usage[i];
            return (
              <li key={cycle.id} className="flex items-baseline gap-2">
                <span className="font-mono text-[12px] font-semibold">{cycle.key}</span>
                <span className="min-w-0 flex-1 truncate">{cycle.label}</span>
                <span className="text-[12px] text-ink-500">
                  {leads === undefined ? "…" : prospects(leads)}
                </span>
              </li>
            );
          })}
        </ul>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(e) => {
              e.preventDefault();
              void confirm();
            }}
          >
            {busy ? "Apagando…" : "Apagar ciclos"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
